import {
  ApiErrorState,
  Badge,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormLayout,
  FormMessage,
  FormSection,
  Input,
  MobileNavDrillProvider,
  Skeleton,
  Textarea,
  toast,
  useMobileNavDrillTop,
} from '@avila-roffe/components-web';
import { zodResolver } from '@hookform/resolvers/zod';
import { Globe, XCircle } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { usePageHeader } from '../../page-header/PageHeaderContext';
import {
  useDeprecateSkillRelease,
  usePublishSkillRelease,
  useSkillRelease,
  useUpdateSkillRelease,
} from './hooks/use-skills';
import type { SkillRelease, SkillReleaseSnapshot } from './types';

// ─── Schema ───────────────────────────────────────────────────────────────────

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  notes: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

function buildDefaults(release: SkillRelease): FormValues {
  return {
    name: release.name,
    notes: release.notes ?? '',
  };
}

function statusVariant(status: string): 'success' | 'warning' | 'secondary' {
  if (status === 'PUBLISHED') return 'success';
  if (status === 'DRAFT') return 'warning';
  return 'secondary';
}

// ─── Snapshot viewer ─────────────────────────────────────────────────────────

function SnapshotField({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
      <p className="text-sm font-mono">{value}</p>
    </div>
  );
}

function SnapshotSection({ snap }: { snap: SkillReleaseSnapshot }) {
  const tags = snap.tags ?? [];
  const tools = snap.compatibleTools ?? [];
  return (
    <FormSection title="Snapshot" asCard>
      <p className="text-xs text-muted-foreground -mt-2 mb-2">
        Immutable — captures the skill state at the time this release was created.
      </p>
      <div className="grid grid-cols-2 gap-4">
        <SnapshotField label="Display Name" value={snap.displayName} />
        <SnapshotField label="Version" value={snap.version ? `v${snap.version}` : null} />
        <SnapshotField label="Scope" value={snap.scope} />
        <SnapshotField label="App" value={snap.appSlug} />
      </div>
      {snap.shortDescription && <SnapshotField label="Description" value={snap.shortDescription} />}
      {tags.length > 0 && (
        <div>
          <p className="text-xs text-muted-foreground mb-1">Tags</p>
          <div className="flex flex-wrap gap-1.5">
            {tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      )}
      {tools.length > 0 && (
        <div>
          <p className="text-xs text-muted-foreground mb-1">Compatible Tools</p>
          <div className="flex flex-wrap gap-1.5">
            {tools.map((t) => (
              <span
                key={t}
                className="inline-flex items-center rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      )}
    </FormSection>
  );
}

// ─── Page inner ───────────────────────────────────────────────────────────────

function SkillReleaseDetailPageInner() {
  const navigate = useNavigate();
  const { appSlug, name, releaseId } = useParams<{
    appSlug: string;
    name: string;
    releaseId: string;
  }>();

  const { data: release, isLoading, isError } = useSkillRelease(appSlug, name, releaseId);
  const updateMutation = useUpdateSkillRelease(appSlug ?? '', name ?? '', releaseId ?? '');
  const publishMutation = usePublishSkillRelease(appSlug ?? '', name ?? '');
  const deprecateMutation = useDeprecateSkillRelease(appSlug ?? '', name ?? '');

  const drillTop = useMobileNavDrillTop();
  const isDraft = release?.status === 'DRAFT';
  const isPublished = release?.status === 'PUBLISHED';

  usePageHeader({
    title: release?.name ?? releaseId ?? '',
    subtitle: release ? `${appSlug}/${name}` : 'Skill Release',
    backPath: `/skills/${appSlug}/${name}?tab=releases`,
    mobileOnBack: drillTop?.onBack,
    action: release ? (
      isDraft ? (
        <Button
          size="sm"
          onClick={() =>
            publishMutation
              .mutateAsync(release.id)
              .then(() => navigate(-1))
              .catch(() => {})
          }
          disabled={publishMutation.isPending}
        >
          <Globe className="mr-1.5 size-3.5" />
          Publish
        </Button>
      ) : isPublished ? (
        <Button
          size="sm"
          variant="destructive"
          onClick={() =>
            deprecateMutation
              .mutateAsync(release.id)
              .then(() => navigate(-1))
              .catch(() => {})
          }
          disabled={deprecateMutation.isPending}
        >
          <XCircle className="mr-1.5 size-3.5" />
          Deprecate
        </Button>
      ) : undefined
    ) : undefined,
    actionKey: release?.status ?? '',
  });

  const form = useForm<FormValues>({
    resolver: isDraft ? zodResolver(schema) : undefined,
    defaultValues: release ? buildDefaults(release) : { name: '', notes: '' },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync when release loads
  useEffect(() => {
    if (release) form.reset(buildDefaults(release));
  }, [release?.id]);

  const isDirty = form.formState.isDirty;

  async function onSubmit(data: FormValues) {
    try {
      await updateMutation.mutateAsync({ name: data.name, notes: data.notes ?? null });
      form.reset(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save');
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4 pt-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (isError || !release) {
    return <ApiErrorState className="mt-6" message="Release not found." />;
  }

  return (
    <Form {...form}>
      <FormLayout
        className="sm:pt-4"
        actionsPosition={isDraft ? 'sticky' : undefined}
        isDirty={isDraft && isDirty}
        cancelAction={
          isDraft
            ? {
                label: 'Discard',
                type: 'button',
                onClick: () => form.reset(buildDefaults(release)),
              }
            : undefined
        }
        submitAction={
          isDraft
            ? {
                label: updateMutation.isPending ? 'Saving…' : 'Save changes',
                type: 'button',
                disabled: updateMutation.isPending,
                onClick: () => void form.handleSubmit(onSubmit)(),
              }
            : undefined
        }
      >
        {/* ── Identity ─────────────────────────────────────────────────── */}
        <FormSection title="Release" asCard>
          <div className="flex items-center gap-2 -mt-1 mb-2">
            <Badge variant={statusVariant(release.status)} appearance="soft" size="sm">
              {release.status}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {new Date(release.createdAt).toLocaleString()}
            </span>
          </div>

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input {...field} disabled={!isDraft} className="font-mono" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    rows={3}
                    className="resize-none"
                    placeholder="What changed in this release…"
                    disabled={!isDraft}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        {/* ── Snapshot ─────────────────────────────────────────────────── */}
        {release.snapshot && <SnapshotSection snap={release.snapshot} />}
      </FormLayout>
    </Form>
  );
}

// ─── Page export ──────────────────────────────────────────────────────────────

export function SkillReleaseDetailPage() {
  return (
    <MobileNavDrillProvider>
      <SkillReleaseDetailPageInner />
    </MobileNavDrillProvider>
  );
}
